import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../../parts/dropdown";
import Modal from "../../../../parts/modal-new";
import {
  defaultsForProduct,
  sourceLabelKey,
} from "../../../../utils/automation-builder";
import {
  AUTOMATION_PRODUCTS,
  groupTriggers,
} from "../../../../utils/email-automations";

const id = "modal-new-automation";
const OPTION_SOURCES = {
  membershipTypes: "membershipTypes",
  locations: "locations",
  coaches: "coaches",
};

const matches = (trigger, search) =>
  `${trigger.name} ${trigger.description}`
    .toLowerCase()
    .includes(search.trim().toLowerCase());

const FilterSelect = ({ field, options, value, onChange }) => {
  const { t } = useTranslation("coaching-portal");
  const choices = field.optionsSource
    ? (options?.[OPTION_SOURCES[field.optionsSource]] ?? [])
    : (field.options ?? []).map((item) => ({ id: item, name: item }));
  const labelOf = (choice) =>
    field.optionsSource
      ? choice.name
      : t(`emailAutomations.trigger.filterValues.${field.key}.${choice.id}`, {
          defaultValue: choice.name,
        });
  if (field.type === "boolean" || field.type === "integer") return null;
  // One location is everyone's: nothing to choose
  if (field.optionsSource === "locations" && choices.length <= 1) return null;
  const anyText = t("emailAutomations.builder.chooser.any");
  const selected = choices.find((choice) => choice.id === value);
  return (
    <div className="nt-select" data-empty={value === "" ? "" : undefined}>
      <span>{t(`emailAutomations.builder.filter.fields.${field.key}`)}</span>
      <Dropdown
        overlay
        summaryText={selected ? labelOf(selected) : anyText}
        selectedValue={value}
        options={[
          { text: anyText, value: "" },
          ...choices.map((choice) => ({
            text: labelOf(choice),
            value: choice.id,
          })),
        ]}
        onSelectFn={onChange}
      />
    </div>
  );
};

FilterSelect.propTypes = {
  field: PropTypes.object.isRequired,
  options: PropTypes.object,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Picking the trigger of a new automation: every trigger of the products the account uses,
// grouped, with its name and the filters it can start with
const NewAutomation = ({
  triggers,
  categories,
  products,
  options,
  product: current,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) =>
    t(`emailAutomations.builder.chooser.${key}`, params);
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState(current);
  const [category, setCategory] = useState("all");
  const [pickedKey, setPickedKey] = useState(null);
  const [name, setName] = useState("");
  const [values, setValues] = useState({});

  const grouped = useMemo(
    () =>
      groupTriggers(
        triggers.filter((trigger) => products.includes(trigger.product)),
        chip === "all" ? current : chip
      ),
    [triggers, products, chip, current]
  );
  const byChip = grouped.filter(
    (trigger) => chip === "all" || trigger.products.includes(chip)
  );
  const found = byChip.filter((trigger) => matches(trigger, search));
  const visible = found.filter(
    (trigger) => category === "all" || trigger.category === category
  );
  const picked =
    visible.find((trigger) => `${trigger.key}|${trigger.name}` === pickedKey) ??
    visible[0];

  const countIn = (key) =>
    found.filter((trigger) => key === "all" || trigger.category === key).length;
  const productFor = (trigger) => {
    if (trigger.products.includes(chip)) return chip;
    if (trigger.products.includes(current)) return current;
    return trigger.products[0];
  };

  const filters = picked
    ? Object.fromEntries(
        picked.filterFields
          .filter((field) => values[field.key])
          .map((field) => [field.key, [values[field.key]]])
      )
    : {};
  const productOfPick = picked ? productFor(picked) : current;

  return (
    <form action="/actions/automations/create" method="POST" data-noreset>
      <input type="hidden" name="product" value={productOfPick} />
      <input type="hidden" name="triggerKey" value={picked?.key ?? ""} />
      <input
        type="hidden"
        name="name"
        value={name.trim() || picked?.name || ""}
      />
      <input type="hidden" name="filters" value={JSON.stringify(filters)} />
      <input
        type="hidden"
        name="exitRules"
        value={JSON.stringify(defaultsForProduct(productOfPick).exitRules)}
      />
      <input
        type="hidden"
        name="reentry"
        value={defaultsForProduct(productOfPick).reentry}
      />
      <Modal name={id} key={id} className="nt-modal" title={tr("title")}>
        <div className="body">
          <p className="nt-subtitle">{tr("subtitle")}</p>
          <div className="nt-toolbar">
            <div className="nt-search">
              <input
                type="search"
                value={search}
                placeholder={tr("search")}
                aria-label={tr("search")}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="nt-chips">
              {[
                "all",
                ...AUTOMATION_PRODUCTS.filter((item) =>
                  products.includes(item)
                ),
              ].map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={chip === item}
                  onClick={() => setChip(item)}
                >
                  {item === "all" ? tr("all") : item.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="nt-columns">
            <nav className="nt-categories" aria-label={tr("title")}>
              {["all", ...categories].map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={category === key}
                  onClick={() => setCategory(key)}
                >
                  <span>
                    {key === "all" ? tr("all") : tr(`categories.${key}`)}
                  </span>
                  <span>{countIn(key)}</span>
                </button>
              ))}
            </nav>
            <ul className="nt-list">
              {visible.length === 0 ? (
                <li className="nt-none">{tr("none")}</li>
              ) : null}
              {visible.map((trigger) => {
                const key = `${trigger.key}|${trigger.name}`;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={
                        trigger === picked
                          ? "nt-item nt-item--selected"
                          : "nt-item"
                      }
                      onClick={() => {
                        setPickedKey(key);
                        setValues({});
                      }}
                    >
                      <strong>{trigger.name}</strong>
                      <span className="nt-item__products">
                        {trigger.products
                          .map((item) => item.toUpperCase())
                          .join(" · ")}
                      </span>
                      <span>{trigger.description}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {picked ? (
              <aside className="nt-detail">
                <header>
                  <small>{tr("selected")}</small>
                  <strong>{picked.name}</strong>
                  <span>{picked.description}</span>
                </header>
                <div className="nt-detail__form">
                  <label className="nt-select">
                    <span>{tr("name")}</span>
                    <input
                      type="text"
                      value={name}
                      maxLength={100}
                      placeholder={tr("namePlaceholder")}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </label>
                  {picked.filterFields.map((field) => (
                    <FilterSelect
                      key={field.key}
                      field={field}
                      options={options}
                      value={values[field.key] ?? ""}
                      onChange={(value) =>
                        setValues((current) => ({
                          ...current,
                          [field.key]: value,
                        }))
                      }
                    />
                  ))}
                </div>
                <span className="nt-source">
                  {tr("source", {
                    source: t(
                      `emailAutomations.builder.trigger.source.${sourceLabelKey(picked.source)}`
                    ),
                  })}
                </span>
              </aside>
            ) : null}
          </div>
        </div>
        <footer className="foot">
          <button type="submit" data-button-type="primary" disabled={!picked}>
            {tr("select")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

NewAutomation.propTypes = {
  triggers: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  options: PropTypes.object,
  product: PropTypes.string.isRequired,
};

export default NewAutomation;
